import { useMemo } from 'react';
import { FormattedNumber } from 'react-intl';

import { useTheme } from '@mui/material';

import { AccountOfferingBalanceAppreciation } from '@arrived/models';

import { PortfolioInfoIcon } from '../../PortfolioInfoIcon';
import { PortfolioTooltip } from '../../PortfolioTooltip';
import { PrivacyContextAwareMoneyValue } from '../../PrivacyContextAwareMoneyValue';
import { CellBodyTypography, CellBodyTypographyProps } from '../CellBodyTypography';

import { AwaitingValuation, AwaitingValuationDescription } from '../../../../common/formattedMessages';

export interface EquityMetricProps extends CellBodyTypographyProps {
  appreciationInfo: AccountOfferingBalanceAppreciation | null;
  enableAwaitingValuationTooltip?: boolean;
}

export const EquityMetric = ({ appreciationInfo, enableAwaitingValuationTooltip, ...rest }: EquityMetricProps) => {
  return appreciationInfo == null ? (
    <CellBodyTypography {...rest}>
      {enableAwaitingValuationTooltip ? (
        <PortfolioTooltip title={<AwaitingValuationDescription />}>
          <span>
            <AwaitingValuation />
            <PortfolioInfoIcon />
          </span>
        </PortfolioTooltip>
      ) : (
        <AwaitingValuation />
      )}
    </CellBodyTypography>
  ) : (
    <EquityMetricContent appreciationInfo={appreciationInfo} {...rest} />
  );
};

interface EquityMetricContentProps extends CellBodyTypographyProps {
  appreciationInfo: AccountOfferingBalanceAppreciation;
}

const EquityMetricContent = ({ appreciationInfo, ...rest }: EquityMetricContentProps) => {
  const { allTimeAppreciationPercent: percent, totalAppreciation: appreciation } = appreciationInfo;
  const theme = useTheme();

  const metricColor = useMemo(() => {
    if (appreciation > 0) {
      return theme.palette.secondary.main;
    } else if (appreciation < 0) {
      return theme.palette.danger;
    }
    return theme.palette.darkened.steel;
  }, [appreciation, theme]);

  const symbol = useMemo(() => (appreciation > 0 ? '+' : null), [appreciation]);

  return (
    <CellBodyTypography color={metricColor} {...rest}>
      {symbol}
      <PrivacyContextAwareMoneyValue value={appreciation} length={5} /> ({symbol}
      <FormattedNumber
        style="percent"
        value={Math.abs(percent) / 100}
        maximumFractionDigits={1}
        minimumFractionDigits={1}
      />
      )
    </CellBodyTypography>
  );
};
